import { Trans } from '@lingui/macro';
import Button from '@material-ui/core/Button';
import PropTypes from 'prop-types';
import { buildCreatePageTarget } from '../AdminLinkUtils';
import Link from '../../links/Link';
import ConfirmDataTableActionButton from '../../form/ConfirmDataTableActionButton';
import { CREATE, DISABLE, ENABLE, IMPORT, PRE_DELETE, RESCAN } from '../../../services/FormDataTableActionService';
import { LIST_NEED_ALERT, MONITORED_INFRASTRUCTURE_FUNCTION_NAME } from '../infrastructure/InfrastructureConstants';

/**
 * Action buttons for monitored infrastructure list.
 * Includes RESCAN, DELETE, DISABLE, ENABLE, CREATE, and IMPORT actions.
 *
 * This component renders all action buttons for the monitored infrastructure list page.
 * Most actions require table selection and are disabled when no items are selected.
 * The RESCAN action uses custom selection params to ensure proper API payload structure.
 *
 * @param {Object} props
 * @param {boolean} props.isDataTableActionButton - Whether selection-based action buttons are disabled
 * @param {boolean} props.dataTableActioning - Whether a DataTable action is in progress
 * @param {Object} props.tableSelection - Current table selection state with {selection, inverted}
 * @param {Object} props.tableState - Current table state
 * @param {Function} props.handleConfirmedDataTableAction - Handler for confirmed actions
 * @param {Function} props.onImportClick - Handler for import button click
 * @param {Object} filterState - search type info
 * @param {Object} selectionData - Combined selection state and rows data
 * @returns {JSX.Element} Action buttons for the infrastructure list
 *
 * @example
 * <MonitoredInfrastructureListActions
 *   isDataTableActionButton={!hasSelection}
 *   dataTableActioning={isProcessing}
 *   tableSelection={selection}
 *   tableState={state}
 *   handleConfirmedDataTableAction={handleAction}
 *   onImportClick={() => setImportDialogOpen(true)}
 *   filterState={}
 * />
 */
export function MonitoredInfrastructureListActions({
    isDataTableActionButton,
    dataTableActioning,
    tableSelection,
    tableState,
    handleConfirmedDataTableAction,
    onImportClick,
    filterState,
    selectionData,
}) {
    const handleRescanAction = (event) => {
        const { inverted, selection } = tableSelection;
        let selectionParams;
        if (inverted) {
            const selectionLength = selectionData.current?.selectionLength;
            selectionParams = {
                count: selectionLength,
                selection,
                inverted,
                valid: true,
            };
        } else {
            selectionParams = {
                count: selection.length,
                selection,
                inverted,
                valid: true,
            };
        }

        const customTableSelection = { selectionParams };
        handleConfirmedDataTableAction(
            event,
            RESCAN,
            null,
            filterState,
            customTableSelection,
            tableState,
            LIST_NEED_ALERT
        );
    };

    return (
        <>
            <ConfirmDataTableActionButton
                key={RESCAN}
                label={<Trans>RESCAN</Trans>}
                disabled={isDataTableActionButton}
                handleConfirmedDataTableAction={handleRescanAction}
                needConfirm={false}
            />
            <ConfirmDataTableActionButton
                key={PRE_DELETE}
                label={<Trans>DELETE</Trans>}
                disabled={isDataTableActionButton}
                handleConfirmedDataTableAction={(event) =>
                    handleConfirmedDataTableAction(
                        event,
                        PRE_DELETE,
                        null,
                        filterState,
                        tableSelection,
                        tableState,
                        LIST_NEED_ALERT
                    )
                }
                needConfirm={false}
            />
            <ConfirmDataTableActionButton
                key={DISABLE}
                label={<Trans>Disable</Trans>}
                disabled={isDataTableActionButton}
                handleConfirmedDataTableAction={(event) =>
                    handleConfirmedDataTableAction(
                        event,
                        DISABLE,
                        null,
                        filterState,
                        tableSelection,
                        tableState,
                        LIST_NEED_ALERT
                    )
                }
                needConfirm={false}
            />
            <ConfirmDataTableActionButton
                key={ENABLE}
                label={<Trans>Enable</Trans>}
                disabled={isDataTableActionButton}
                handleConfirmedDataTableAction={(event) =>
                    handleConfirmedDataTableAction(
                        event,
                        ENABLE,
                        null,
                        filterState,
                        tableSelection,
                        tableState,
                        LIST_NEED_ALERT
                    )
                }
                needConfirm={false}
            />
            <Button
                key={CREATE}
                component={Link}
                disabled={dataTableActioning}
                to={buildCreatePageTarget(MONITORED_INFRASTRUCTURE_FUNCTION_NAME)}
                variant="contained"
                color="primary"
            >
                <Trans>Create</Trans>
            </Button>
            <Button
                key={IMPORT}
                disabled={dataTableActioning}
                onClick={onImportClick}
                variant="contained"
                color="primary"
            >
                <Trans>Import</Trans>
            </Button>
        </>
    );
}

MonitoredInfrastructureListActions.propTypes = {
    isDataTableActionButton: PropTypes.bool.isRequired,
    dataTableActioning: PropTypes.bool.isRequired,
    tableSelection: PropTypes.shape({
        selection: PropTypes.array.isRequired,
        inverted: PropTypes.bool.isRequired,
    }),
    tableState: PropTypes.object,
    handleConfirmedDataTableAction: PropTypes.func.isRequired,
    onImportClick: PropTypes.func.isRequired,
    filterState: PropTypes.object,
    selectionData: PropTypes.shape({
        current: PropTypes.object,
    }),
};

MonitoredInfrastructureListActions.defaultProps = {
    tableSelection: { selection: [], inverted: false },
    tableState: {},
    filterState: {},
    selectionData: null,
};
